import { useEffect } from 'react';

type WakeLockNavigator = Navigator & {
  wakeLock?: {
    request: (type: 'screen') => Promise<WakeLockSentinelLike>;
  };
};

type WakeLockSentinelLike = {
  release: () => Promise<void>;
  released: boolean;
  addEventListener: (type: 'release', cb: () => void) => void;
};

const warnedLabels = new Set<string>();

function shortUA(): string {
  const ua = navigator.userAgent ?? '';
  const m = ua.match(
    /(CriOS|FxiOS|EdgiOS|Chrome|Firefox|Edg|Safari)\/[\d.]+/
  );
  return (m?.[0] ?? ua.slice(0, 60)) || 'unknown';
}

export function useWakeLock(active: boolean, label = 'wakelock'): void {
  useEffect(() => {
    if (!active) return;
    const nav = navigator as WakeLockNavigator;
    if (!nav.wakeLock) {
      if (!warnedLabels.has(label)) {
        warnedLabels.add(label);
        console.warn(
          `[${label}] wake lock API unavailable ` +
            `(ua=${shortUA()} secureContext=${window.isSecureContext} ` +
            `standalone=${window.matchMedia('(display-mode: standalone)').matches})`
        );
      }
      return;
    }

    let sentinel: WakeLockSentinelLike | null = null;
    let cancelled = false;
    let reacquireTimer: ReturnType<typeof setTimeout> | undefined;

    const acquire = async () => {
      if (cancelled || sentinel) return;
      if (document.visibilityState !== 'visible') return;
      try {
        const s = await nav.wakeLock!.request('screen');
        if (cancelled) {
          void s.release();
          return;
        }
        sentinel = s;
        console.log(`[${label}] acquired`);
        s.addEventListener('release', () => {
          const wasOurs = sentinel === s;
          if (wasOurs) sentinel = null;
          console.log(
            `[${label}] released by system (cancelled=${cancelled}, visible=${document.visibilityState})`
          );
          if (!cancelled && document.visibilityState === 'visible' && wasOurs) {
            // System released while we still want it. Re-acquire after a short
            // backoff to avoid hammering if something is blocking acquisition.
            reacquireTimer = setTimeout(() => {
              reacquireTimer = undefined;
              void acquire();
            }, 250);
          }
        });
      } catch (e) {
        console.warn(`[${label}] acquire failed`, e);
      }
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible' && !sentinel && !cancelled) {
        void acquire();
      }
    };

    void acquire();
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      if (reacquireTimer !== undefined) {
        clearTimeout(reacquireTimer);
        reacquireTimer = undefined;
      }
      if (sentinel) {
        const s = sentinel;
        sentinel = null;
        void s.release().then(
          () => console.log(`[${label}] released (cleanup)`),
          (e) => console.warn(`[${label}] cleanup release failed`, e)
        );
      }
    };
  }, [active, label]);
}
